import { proficiencyOrderingValue } from '@/lib/assignment/unknownProficiency';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Scissors, User, Clock, Wrench, Loader2, AlertTriangle, ArrowRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { SplitOption } from '@/utils/generateSplitOptions';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SplitPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  splitOptions: SplitOption[];
  onConfirm: (optionIndex: number) => void;
  isProcessing: boolean;
  onViewNextOption?: () => void;
  currentOptionIndex?: number;
  totalOptions?: number;
  originalMakespan?: number; // Total time if kept together
}

export const SplitPreviewDialog = ({
  isOpen,
  onClose,
  splitOptions,
  onConfirm,
  isProcessing,
  onViewNextOption,
  currentOptionIndex = 0,
  totalOptions = 1,
  originalMakespan,
}: SplitPreviewDialogProps) => {
  const isLoading = splitOptions.length === 0;

  // Calculate competence score: C = 0.7·proficiency + 0.3·skillLevel (normalized to 0-100)
  const calculateCompetenceScore = (proficiency: number, skillLevel: number): number => {
    const normalizedSkill = (skillLevel / 10) * 100; // Skill 1-10 → 0-100
    return (0.7 * proficiency) + (0.3 * normalizedSkill);
  };

  // Calculate makespan for a split option (max of both parts)
  const calculateSplitMakespan = (option: SplitOption): number | null => {
    if (!option.partA.scheduledStart || !option.partA.scheduledFinish || 
        !option.partB.scheduledStart || !option.partB.scheduledFinish) {
      return null;
    }

    const partAStart = new Date(option.partA.scheduledStart).getTime();
    const partAFinish = new Date(option.partA.scheduledFinish).getTime();
    const partBStart = new Date(option.partB.scheduledStart).getTime();
    const partBFinish = new Date(option.partB.scheduledFinish).getTime();

    // Makespan = time from earliest start to latest finish
    const earliestStart = Math.min(partAStart, partBStart);
    const latestFinish = Math.max(partAFinish, partBFinish);

    return Math.round((latestFinish - earliestStart) / 60000); // Convert to minutes
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Preview Ticket Split Options
          </DialogTitle>
          <DialogDescription>
            Compare how splitting affects total time and tech assignments
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Analyzing split options and calculating times...</p>
              <p className="text-xs text-muted-foreground">This may take a few moments</p>
            </div>
          ) : (
            splitOptions.map((option, index) => {
              const splitMakespan = calculateSplitMakespan(option);
              const makespanSavings = originalMakespan && splitMakespan 
                ? originalMakespan - splitMakespan 
                : null;
              
              // Calculate competence scores for each part
              const partACompetence = calculateCompetenceScore(
                proficiencyOrderingValue(option.partA.proficiencyMatch),
                option.partA.avgSkillLevel
              );
              const partBCompetence = calculateCompetenceScore(
                proficiencyOrderingValue(option.partB.proficiencyMatch),
                option.partB.avgSkillLevel
              );

              return (
                <Card key={index} className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span>Option {index + 1}: {option.label}</span>
                      <Badge variant={option.type === 'proficiency' ? 'default' : 'secondary'}>
                        {option.type}
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">{option.reasoning}</p>

                    {/* Makespan Comparison */}
                    {originalMakespan && splitMakespan && makespanSavings !== null && (
                      <Alert className={makespanSavings > 0 ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 
                                       makespanSavings < 0 ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' : 
                                       'border-blue-500 bg-blue-50 dark:bg-blue-950/20'}>
                        <div className="flex items-center gap-2">
                          {makespanSavings > 0 ? (
                            <>
                              <TrendingDown className="h-4 w-4 text-green-600" />
                              <AlertDescription className="font-medium text-green-700 dark:text-green-400">
                                Makespan Improvement: Save {makespanSavings} minutes 
                                <span className="text-xs ml-2">
                                  ({originalMakespan} min → {splitMakespan} min)
                                </span>
                              </AlertDescription>
                            </>
                          ) : makespanSavings < 0 ? (
                            <>
                              <TrendingUp className="h-4 w-4 text-amber-600" />
                              <AlertDescription className="font-medium text-amber-700 dark:text-amber-400">
                                Makespan Increase: +{Math.abs(makespanSavings)} minutes 
                                <span className="text-xs ml-2">
                                  ({originalMakespan} min → {splitMakespan} min)
                                </span>
                              </AlertDescription>
                            </>
                          ) : (
                            <>
                              <Minus className="h-4 w-4 text-blue-600" />
                              <AlertDescription className="font-medium text-blue-700 dark:text-blue-400">
                                Makespan Unchanged: {splitMakespan} minutes
                              </AlertDescription>
                            </>
                          )}
                        </div>
                      </Alert>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Part A */}
                      <div className="border rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/20">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                            A
                          </div>
                          <div>
                            <h4 className="font-semibold">Part A</h4>
                            <p className="text-xs text-muted-foreground">
                              {option.partA.jobs.length} job{option.partA.jobs.length !== 1 ? 's' : ''} • {option.partA.totalMinutes} min
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 mb-3">
                          {option.partA.jobs.map((job, jobIdx) => (
                            <div key={jobIdx} className="text-sm flex items-center gap-2">
                              <Wrench className="h-3 w-3 text-muted-foreground" />
                              <span className="flex-1">{job.name}</span>
                              <Badge variant="outline" className="text-xs">
                                L{job.skill}
                              </Badge>
                            </div>
                          ))}
                        </div>

                        <div className="border-t pt-3 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {option.partA.suggestedTechName || 'No tech available'}
                            </span>
                          </div>

                          {/* Competence Score */}
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="font-medium">Competence Score</span>
                              <span className="font-bold">{partACompetence.toFixed(1)}/100</span>
                            </div>
                            <Progress 
                              value={partACompetence} 
                              className="h-2"
                            />
                            <p className="text-xs text-muted-foreground">
                              70% proficiency ({option.partA.proficiencyMatch != null ? `${option.partA.proficiencyMatch.toFixed(0)}%` : 'no OptiBay history yet'}) + 30% skill level (L{option.partA.avgSkillLevel})
                            </p>
                          </div>

                          {option.partA.scheduledStart && option.partA.scheduledFinish ? (
                            <>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <span className="text-xs text-muted-foreground">Start: </span>
                                  <span className="font-medium">{new Date(option.partA.scheduledStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className={`h-4 w-4 ${option.partA.dueOutViolation ? 'text-destructive' : 'text-muted-foreground'}`} />
                                <div>
                                  <span className="text-xs text-muted-foreground">Finish: </span>
                                  <span className={`font-medium ${option.partA.dueOutViolation ? 'text-destructive' : ''}`}>
                                    {new Date(option.partA.scheduledFinish).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                  {option.partA.dueOutViolation && (
                                    <AlertTriangle className="inline h-3 w-3 ml-1 text-destructive" />
                                  )}
                                </div>
                              </div>
                              {option.partA.dueOutViolation && (
                                <div className="text-xs text-destructive flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span>Exceeds due-out time</span>
                                </div>
                              )}
                              {/* Show sequential badge if Part A starts after Part B */}
                              {option.partB.scheduledFinish && 
                               new Date(option.partA.scheduledStart) > new Date(option.partB.scheduledFinish) && (
                                <Badge variant="secondary" className="text-xs">
                                  <ArrowRight className="h-3 w-3 mr-1" />
                                  Sequential (after Part B)
                                </Badge>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span className="text-xs">Calculating times...</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Part B */}
                      <div className="border rounded-lg p-4 bg-green-50/50 dark:bg-green-950/20">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                            B
                          </div>
                          <div>
                            <h4 className="font-semibold">Part B</h4>
                            <p className="text-xs text-muted-foreground">
                              {option.partB.jobs.length} job{option.partB.jobs.length !== 1 ? 's' : ''} • {option.partB.totalMinutes} min
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 mb-3">
                          {option.partB.jobs.map((job, jobIdx) => (
                            <div key={jobIdx} className="text-sm flex items-center gap-2">
                              <Wrench className="h-3 w-3 text-muted-foreground" />
                              <span className="flex-1">{job.name}</span>
                              <Badge variant="outline" className="text-xs">
                                L{job.skill}
                              </Badge>
                            </div>
                          ))}
                        </div>

                        <div className="border-t pt-3 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {option.partB.suggestedTechName || 'No tech available'}
                            </span>
                          </div>

                          {/* Competence Score */}
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="font-medium">Competence Score</span>
                              <span className="font-bold">{partBCompetence.toFixed(1)}/100</span>
                            </div>
                            <Progress 
                              value={partBCompetence} 
                              className="h-2"
                            />
                            <p className="text-xs text-muted-foreground">
                              70% proficiency ({option.partB.proficiencyMatch != null ? `${option.partB.proficiencyMatch.toFixed(0)}%` : 'no OptiBay history yet'}) + 30% skill level (L{option.partB.avgSkillLevel})
                            </p>
                          </div>

                          {option.partB.scheduledStart && option.partB.scheduledFinish ? (
                            <>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <span className="text-xs text-muted-foreground">Start: </span>
                                  <span className="font-medium">{new Date(option.partB.scheduledStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className={`h-4 w-4 ${option.partB.dueOutViolation ? 'text-destructive' : 'text-muted-foreground'}`} />
                                <div>
                                  <span className="text-xs text-muted-foreground">Finish: </span>
                                  <span className={`font-medium ${option.partB.dueOutViolation ? 'text-destructive' : ''}`}>
                                    {new Date(option.partB.scheduledFinish).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                  {option.partB.dueOutViolation && (
                                    <AlertTriangle className="inline h-3 w-3 ml-1 text-destructive" />
                                  )}
                                </div>
                              </div>
                              {option.partB.dueOutViolation && (
                                <div className="text-xs text-destructive flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span>Exceeds due-out time</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span className="text-xs">Calculating times...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button
                      className="w-full mt-4"
                      onClick={() => onConfirm(index)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating split tickets...
                        </>
                      ) : (
                        <>
                          <Scissors className="mr-2 h-4 w-4" />
                          Use This Split
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}

          {!isLoading && (
            <div className="space-y-2">
              {totalOptions > 1 && onViewNextOption && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={onViewNextOption}
                  disabled={isProcessing}
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  View Next Split Option ({currentOptionIndex + 1}/{totalOptions})
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={onClose}
                disabled={isProcessing}
              >
                Don't Split - Keep Ticket Together
                {originalMakespan && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({originalMakespan} min total)
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
